import { LogisticsProjectLookupComponent } from '../../../logistics-projects/logistics-project-lookup.component';
import * as XLSX from 'xlsx';
import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AccountingReportsService, ReportDiagnostic } from "../../accounting-reports.service";
import { TrialBalance, TrialBalanceTreeItem } from "./trial-balance.model";
import { SessionStorageService } from "../../../services/session-storage.service";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { IftaLabelModule } from 'primeng/iftalabel';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ReportEmailComponent } from '../shared/report-email.component';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { AccountingReportTreeCellComponent } from '../shared/accounting-report-tree-cell.component';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { closeFinancialReportPdf, downloadFinancialReportPdf, openFinancialReportPdf } from '../shared/financial-report-pdf';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-trial-balance',
    templateUrl: './trial-balance.component.html',
    styleUrls: [
        './trial-balance.component.css',
        '../shared/accounting-report-tree-table.css'
    ],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        ButtonModule,
        TableModule,
        SelectModule,
        DatePickerModule,
        ToastModule,
        CardModule,
        ProgressBarModule,
        DividerModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        PageDataPanelComponent,
        ReportEmailComponent,
        AccountingReportTreeCellComponent
    ],
    providers: [
        MessageService,
        AccountingReportsService,
        ChartOfAccountsService,
        SessionStorageService
    ]
})
export class TrialBalanceComponent implements OnInit {
    readonly reportActions = new PendingActionTracker<string>();

    trialBalanceItems: TrialBalance[] = [];
    visibleTrialBalanceItems: TrialBalanceTreeItem[] = [];
    private hierarchicalTrialBalanceItems: TrialBalanceTreeItem[] = [];
    isLoading: boolean = false;
    totalRecords: number = 0;
    first: number = 0;

    startDate: Date;
    endDate: Date;

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    // New properties for Trial Balance totals
    totalDebitsSum: number = 0;
    totalCreditsSum: number = 0;
    netDifference: number = 0; // Should ideally be 0 for a balanced trial balance
    reportCurrencyCode: string = 'SR'; // Default, will be updated based on data
    diagnostics: ReportDiagnostic[] = [];

    selectedLogisticsProjectId?: number | null;

    constructor(private accountReportService: AccountingReportsService,
        private chartOfAccountsService: ChartOfAccountsService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService) { }

    ngOnInit(): void {
        const today = new Date();
        // Set initial dates for current month
        this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        this.endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Reports' }, { label: 'Trial Balance' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.loadTrialBalanceData({});
    }

    loadTrialBalanceData(event: TableLazyLoadEvent) {
        if (!this.startDate || !this.endDate) {
            // Don't load if critical parameters are missing
            // You might want to show a message to the user here
            return;
        }

        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company information is missing.' });
            return;
        }

        this.isLoading = true;

        forkJoin({
            report: this.accountReportService.getTrialBalanceV2(
                companyId,
                this.startDate.getTime(),
                this.endDate.getTime(),
                this.selectedLogisticsProjectId
            ),
            coaTree: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, true)
        }).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: ({ report, coaTree }) => {
                const data = report.rows || [];
                this.diagnostics = report.diagnostics ?? [];
                this.trialBalanceItems = data;
                this.hierarchicalTrialBalanceItems = this.transformToTree(data, coaTree ?? []);
                this.refreshVisibleTree();

                if (data.length > 0) {
                    this.reportCurrencyCode = data[0].currencyCode;
                } else {
                    this.reportCurrencyCode = 'SR'; // Fallback
                }

                this.calculateTotals();
            },
            error: error => {
                console.error('Error fetching Trial Balance data:', error);
                this.trialBalanceItems = [];
                this.hierarchicalTrialBalanceItems = [];
                this.visibleTrialBalanceItems = [];
                this.totalRecords = 0;
                this.diagnostics = [];
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load trial balance data.' });
            }
        });
    }

    private transformToTree(rows: TrialBalance[], coaTree: ChartOfAccounts[]): TrialBalanceTreeItem[] {
        const rowsByCode = new Map(rows.map(row => [row.accountCode, row]));
        const usedCodes = new Set<string>();

        const zeroRow = (account: ChartOfAccounts): TrialBalance => ({
            owningCompanyId: account.owningCompanyId ?? 0,
            currencyCode: account.currencyCode || this.reportCurrencyCode,
            accountId: account.accountId,
            accountCode: account.accountCode,
            accountName: account.accountName,
            internalType: account.internalType || account.accountType?.accountCategory?.toLowerCase() || '',
            openingBalance: 0,
            totalDebit: 0,
            totalCredit: 0,
            closingBalance: 0,
            closingDebit: 0,
            closingCredit: 0,
            balance: 0
        });

        const numericFields: Array<keyof Pick<TrialBalance,
            'openingBalance' | 'totalDebit' | 'totalCredit' | 'closingBalance' | 'closingDebit' | 'closingCredit' | 'balance'>> = [
            'openingBalance', 'totalDebit', 'totalCredit', 'closingBalance', 'closingDebit', 'closingCredit', 'balance'
        ];

        const buildNode = (account: ChartOfAccounts, level: number): TrialBalanceTreeItem => {
            const directRow = rowsByCode.get(account.accountCode) ?? zeroRow(account);
            if (rowsByCode.has(account.accountCode)) {
                usedCodes.add(account.accountCode);
            }

            const children = (account.childrenAccounts ?? []).map(child => buildNode(child, level + 1));
            const hasDirectActivity = numericFields.some(field => Math.abs(Number(directRow[field] ?? 0)) >= 0.005);

            if (children.length && hasDirectActivity) {
                children.unshift({
                    ...directRow,
                    accountId: -(account.accountId || 1),
                    accountCode: `${account.accountCode}:DIRECT`,
                    accountName: `Direct activity in ${account.accountName}`,
                    level: level + 1,
                    isDirectPostingRow: true,
                    expanded: false,
                    children: []
                });
            }

            const item: TrialBalanceTreeItem = {
                ...directRow,
                accountId: account.accountId,
                accountCode: account.accountCode,
                accountName: account.accountName,
                internalType: directRow.internalType || account.internalType || account.accountType?.accountCategory?.toLowerCase() || '',
                level,
                isSummaryRow: children.length > 0,
                expanded: level < 1,
                children
            };

            if (children.length) {
                numericFields.forEach(field => {
                    (item as unknown as Record<string, number>)[field] = children.reduce(
                        (sum, child) => sum + Number(child[field] ?? 0),
                        0
                    );
                });
            }
            return item;
        };

        const tree = coaTree.map(root => buildNode(root, 0));
        rows.filter(row => !usedCodes.has(row.accountCode)).forEach(row => {
            tree.push({
                ...row,
                level: 0,
                isSummaryRow: false,
                expanded: false,
                children: []
            });
        });
        return tree;
    }

    private refreshVisibleTree(): void {
        const visible: TrialBalanceTreeItem[] = [];
        const append = (items: TrialBalanceTreeItem[]) => {
            items.forEach(item => {
                visible.push(item);
                if (item.expanded && item.children?.length) {
                    append(item.children);
                }
            });
        };
        append(this.hierarchicalTrialBalanceItems);
        this.visibleTrialBalanceItems = visible;
        this.totalRecords = visible.length;
        this.first = 0;
    }

    toggleNode(item: TrialBalanceTreeItem): void {
        item.expanded = !item.expanded;
        this.refreshVisibleTree();
    }

    getBalanceSide(item: TrialBalanceTreeItem, value: number | null | undefined): 'DR' | 'CR' | 'ZERO' {
        const amount = Number(value ?? 0);
        if (Math.abs(amount) < 0.005) {
            return 'ZERO';
        }
        const normalSide = item.internalType?.toLowerCase() === 'asset' || item.internalType?.toLowerCase() === 'expense' ? 'DR' : 'CR';
        if (amount > 0) {
            return normalSide;
        }
        return normalSide === 'DR' ? 'CR' : 'DR';
    }

    calculateTotals(): void {
        let debits = 0;
        let credits = 0;

        this.trialBalanceItems.forEach(item => {
            const totalDebit = parseFloat((item.closingDebit ?? item.totalDebit ?? 0).toString());
            const totalCredit = parseFloat((item.closingCredit ?? item.totalCredit ?? 0).toString());

            debits += totalDebit;
            credits += totalCredit;
        });

        this.totalDebitsSum = debits;
        this.totalCreditsSum = credits;
        // Calculate net difference. Should be close to zero for a balanced trial balance.
        this.netDifference = debits - credits;

    }

    exportExcel(): void {
        if (!this.trialBalanceItems || this.trialBalanceItems.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'No data to export.' });
            return;
        }

        // Define the type for the objects in dataToExport for better type safety
        type ExcelRow = {
            'Account Code': string | number; // Allow number for totalDebitsSum etc.
            'Account Name': string;
            'Internal Type': string;
            'Opening Balance': string | number;
            'Period Debit': string | number;
            'Period Credit': string | number;
            'Closing Debit': string | number;
            'Closing Credit': string | number;
            'Closing Balance': string | number;
        };

        const dataToExport: ExcelRow[] = this.trialBalanceItems.map(item => ({
            'Account Code': item.accountCode,
            'Account Name': item.accountName,
            'Internal Type': item.internalType,
            'Opening Balance': item.openingBalance,
            'Period Debit': item.totalDebit,
            'Period Credit': item.totalCredit,
            'Closing Debit': item.closingDebit,
            'Closing Credit': item.closingCredit,
            'Closing Balance': item.closingBalance
        }));

        // Add an empty row for spacing - provide all keys with empty strings
        dataToExport.push({
            'Account Code': '',
            'Account Name': '',
            'Internal Type': '',
            'Opening Balance': 0,
            'Period Debit': 0,
            'Period Credit': 0,
            'Closing Debit': 0,
            'Closing Credit': 0,
            'Closing Balance': 0
        });

        dataToExport.push({
            'Account Code': 'Grand Totals:',
            'Account Name': '',
            'Internal Type': '',
            'Opening Balance': '',
            'Period Debit': '',
            'Period Credit': '',
            'Closing Debit': this.totalDebitsSum,
            'Closing Credit': this.totalCreditsSum,
            'Closing Balance': this.netDifference
        });

        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);

        // Optional: Adjust column widths
        const wscols = [
            { wch: 15 }, // Account Code
            { wch: 40 }, // Account Name
            { wch: 20 }, // Internal Type
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 }
        ];
        worksheet['!cols'] = wscols;

        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "TrialBalanceReport");
        this.messageService.add({ severity: 'success', summary: 'Exported', detail: 'Report exported to Excel.' });
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const downloadLink: HTMLAnchorElement = document.createElement('a');
        const url = URL.createObjectURL(data);
        downloadLink.href = url;
        downloadLink.download = fileName + '_' + new Date().toISOString().slice(0, 10) + '.xlsx';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
    }

    printReport(download = false): void {
        const action = download ? 'download' : 'print';
        if (!this.reportActions.begin(action)) return;
        const target = download ? null : window.open('', '_blank');
        this.accountReportService.printFinanceReport('trial-balance', this.sessionStorageService.getCompany()?.id ?? 0, {
            startDate: this.startDate,
            endDate: this.endDate,
            logisticsProjectId: this.selectedLogisticsProjectId
        }).pipe(finalize(() => this.reportActions.end(action))).subscribe({
            next: blob => download ? downloadFinancialReportPdf(blob, 'trial-balance') : openFinancialReportPdf(blob, target),
            error: () => {
                closeFinancialReportPdf(target);
                this.messageService.add({ severity: 'error', summary: 'Print unavailable', detail: 'Failed to generate the Trial Balance PDF.' });
            }
        });
    }
}
